import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowUpRight, GitMerge, RotateCcw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { MERGE_CONFIRM_WORD, type PoolMergePreview, type PoolRow, type PoolsAndTagsView, type TagAdminRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { ARCHIVED_TONE } from '../../../components/status-tones';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { ConfirmTyped } from '../../admin/components/simple-list/ConfirmTyped';
import { ListTable, type ListColumn } from '../../admin/components/simple-list/ListTable';
import { FilterPills, ListNoMatch, ListSearch, ListTabs, ListToolbar, PanelActions, PanelFacts, PanelSection, SidePanel, SimpleListPage, usePanelMode } from '../../admin/components/simple-list/SimpleList';
import { oneOf, useListAddress } from '../../admin/components/simple-list/use-list-address';
import { useRowChanges, type RowCache, type RowChanges } from '../../admin/components/simple-list/use-row-changes';
import { CANDIDATE_SEARCH_KEY, POOLS_KEY, poolsApi, useMergePreview, usePoolsAndTags } from '../api/pools-api';

const PATH = '/admin/pools-and-tags';
const TABS = ['pools', 'tags'] as const;
const FILTERS = ['in_use', 'off'] as const;
type Filter = (typeof FILTERS)[number];
const FEWEST_FIRST = 'fewest';
const VIEW_KEY = [...POOLS_KEY, 'admin'] as const;

const TAG_ROWS: RowCache<PoolsAndTagsView, TagAdminRow> = {
  queryKey: VIEW_KEY,
  rowsOf: (view) => view.tags,
  withRows: (view, tags) => ({ ...view, tags }),
  idOf: (row) => row.id,
  alsoRefresh: [['candidates'], CANDIDATE_SEARCH_KEY],
};

const POOL_ROWS: RowCache<PoolsAndTagsView, PoolRow> = {
  queryKey: VIEW_KEY,
  rowsOf: (view) => view.pools,
  withRows: (view, pools) => ({ ...view, pools }),
  idOf: (row) => row.id,
  alsoRefresh: [POOLS_KEY],
};

/**
 * ADMIN-11 (F19.13) — keeping pools and tags tidy, in the simple-list pattern (Admin redesign batch 2, option A): two
 * tabs, filter pills, "Fewest people first" to find near-duplicates, and the picked one in the panel. Rename, Retire and
 * Bring back happen at once with Undo. **Merge can't be undone**, so the panel says exactly what it will do — counted
 * in distinct people by the server — and Merge asks beside its button for the word "merge". A merged tag is deleted (it
 * leaves every list; its history stays); a merged pool is archived. Nobody's application changes. Admin and HR.
 */
export function PoolsAndTagsPage(): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const view = usePoolsAndTags(canEdit);
  const tagChanges = useRowChanges(TAG_ROWS);
  const poolChanges = useRowChanges(POOL_ROWS);
  const mode = usePanelMode();
  const [address, setAddress] = useListAddress(PATH);
  const tab = oneOf(address.tab, TABS, 'pools');
  const filter = FILTERS.find((value) => value === address.show) ?? null;
  const isFewestFirst = address.sort === FEWEST_FIRST;
  const query = address.q ?? '';

  if (!canEdit) return <ForbiddenState resource="pools and tags" contact="Your admin" />;
  if (view.isPending) return <SkeletonLines count={10} />;
  if (view.error && !view.data) return <ServerErrorState onRetry={() => void view.refetch()} />;

  const { pools, tags } = view.data;
  const close = (): void => setAddress({ open: undefined });
  const pick = (id: string): void => setAddress({ open: id });
  const head = {
    crumb: 'Admin · Hiring setup',
    title: 'Pools and tags',
    lede: 'Keep pools and tags tidy: rename, merge one into another, retire a tag or bring an archived pool back. Merging moves everyone across; nobody’s application changes.',
    tabs: (
      <ListTabs
        label="Pools or tags"
        value={tab}
        options={[
          { value: 'pools', label: 'Talent pools', count: pools.length },
          { value: 'tags', label: 'Tags', count: tags.length },
        ]}
        onChange={(next) => setAddress({ tab: next === 'pools' ? undefined : next, open: undefined, show: undefined })}
      />
    ),
  } as const;
  const toolbar = (counts: { readonly inUse: number; readonly off: number }, offLabel: string, what: string): ReactNode => (
    <ListToolbar search={<ListSearch label={`Find a ${what}`} value={query} onChange={(next) => setAddress({ q: next })} />}>
      <FilterPills
        label={`Show ${what}s that are`}
        value={filter}
        options={[
          { value: 'in_use', label: 'In use', count: counts.inUse },
          { value: 'off', label: offLabel, count: counts.off },
        ]}
        onChange={(next) => setAddress({ show: next ?? undefined })}
      />
      <FilterPills label="Order" value={isFewestFirst ? FEWEST_FIRST : null} options={[{ value: FEWEST_FIRST, label: 'Fewest people first' }]} onChange={(next) => setAddress({ sort: next ?? undefined })} />
    </ListToolbar>
  );

  if (tab === 'tags') {
    const shown = ordered(tags.filter((row) => matchesOff(row.isRetired, filter) && matches(row.name, query)), (row) => row.peopleCount, isFewestFirst);
    const open = tags.find((row) => row.id === address.open) ?? null;
    const columns: ListColumn<TagAdminRow>[] = [{ key: 'people', header: 'People', isNumeric: true, render: (row) => peopleText(row.peopleCount) }];
    return (
      <SimpleListPage
        {...head}
        toolbar={toolbar({ inUse: tags.filter((row) => !row.isRetired).length, off: tags.filter((row) => row.isRetired).length }, 'Retired', 'tag')}
        panel={
          open && (
            <SidePanel mode={mode} label={`Tag: ${open.name}`} title={open.name} sub={open.isRetired && <ToneChip tone={ARCHIVED_TONE}>Retired</ToneChip>} onClose={close}>
              <TagPanel key={open.id} row={open} others={tags.filter((other) => other.id !== open.id && !other.isRetired)} changes={tagChanges} onMerged={pick} />
            </SidePanel>
          )
        }
      >
        <ListTable
          label="Tags"
          name={{ header: 'Tag', label: (row) => row.name, render: (row) => <TagName row={row} /> }}
          columns={columns}
          groups={[{ key: 'all', rows: shown }]}
          idOf={(row) => row.id}
          selectedId={open?.id ?? null}
          onOpen={(row) => pick(row.id)}
          isMuted={(row) => row.isRetired}
          trailing={(row) =>
            row.isRetired && (
              <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => setTagRetired(tagChanges, row, false)}>
                Bring back
              </Button>
            )
          }
          empty={tags.length === 0 ? <span>No tags yet. Tags are added from the pipeline’s bulk actions.</span> : <ListNoMatch what="tag" query={query} onClear={() => setAddress({ q: undefined, show: undefined })} />}
        />
      </SimpleListPage>
    );
  }

  const shown = ordered(pools.filter((row) => matchesOff(row.isArchived, filter) && matches(row.name, query)), (row) => row.memberCount, isFewestFirst);
  const open = pools.find((row) => row.id === address.open) ?? null;
  const columns: ListColumn<PoolRow>[] = [
    { key: 'people', header: 'People', isNumeric: true, render: (row) => peopleText(row.memberCount) },
    { key: 'owner', header: 'Owner', isWideOnly: true, render: (row) => row.ownerName },
  ];
  return (
    <SimpleListPage
      {...head}
      toolbar={toolbar({ inUse: pools.filter((row) => !row.isArchived).length, off: pools.filter((row) => row.isArchived).length }, 'Archived', 'pool')}
      panel={
        open && (
          <SidePanel mode={mode} label={`Pool: ${open.name}`} title={open.name} sub={<PoolChips row={open} />} onClose={close}>
            <PoolPanel key={open.id} row={open} others={pools.filter((other) => other.id !== open.id && !other.isArchived)} changes={poolChanges} onMerged={pick} />
          </SidePanel>
        )
      }
    >
      <ListTable
        label="Talent pools"
        name={{ header: 'Pool', label: (row) => row.name, render: (row) => <PoolName row={row} /> }}
        columns={columns}
        groups={[{ key: 'all', rows: shown }]}
        idOf={(row) => row.id}
        selectedId={open?.id ?? null}
        onOpen={(row) => pick(row.id)}
        isMuted={(row) => row.isArchived}
        phoneLine={(row) => row.ownerName}
        trailing={(row) =>
          row.isArchived && (
            <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => setPoolArchived(poolChanges, row, false)}>
              Bring back
            </Button>
          )
        }
        empty={pools.length === 0 ? <span>No pools yet. Pools are made from Talent pools, or when someone is rejected with a pool reason.</span> : <ListNoMatch what="pool" query={query} onClear={() => setAddress({ q: undefined, show: undefined })} />}
      />
    </SimpleListPage>
  );
}

function TagName({ row }: { readonly row: TagAdminRow }): ReactNode {
  return (
    <>
      {row.name}
      {row.isRetired && <ToneChip tone={ARCHIVED_TONE}>Retired</ToneChip>}
    </>
  );
}

function PoolName({ row }: { readonly row: PoolRow }): ReactNode {
  return (
    <>
      {row.name}
      <PoolChips row={row} />
    </>
  );
}

function PoolChips({ row }: { readonly row: PoolRow }): ReactNode {
  return (
    <>
      {row.isSystem && (
        <Chip tone="progress" plain>
          System
        </Chip>
      )}
      {row.isArchived && <ToneChip tone={ARCHIVED_TONE}>Archived</ToneChip>}
    </>
  );
}

function TagPanel({ row, others, changes, onMerged }: { readonly row: TagAdminRow; readonly others: readonly TagAdminRow[]; readonly changes: RowChanges<TagAdminRow>; readonly onMerged: (id: string) => void }): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  const name = draft ?? row.name;
  const trimmed = name.trim().replace(/\s+/g, ' ');
  const isValid = trimmed.length >= 1 && trimmed.length <= 60;
  const isChanged = trimmed !== row.name;

  const rename = (): void => {
    if (!isValid || !isChanged) return;
    const before = row.name;
    setDraft(null);
    changes.changeWithUndo(
      { id: row.id, patch: { name: trimmed }, send: async (version) => (await poolsApi.renameTag(row.id, trimmed, version)).version, failure: "The tag wasn't renamed" },
      { message: `Renamed to “${trimmed}”`, undo: { id: row.id, patch: { name: before }, send: async (version) => (await poolsApi.renameTag(row.id, before, version)).version, failure: "The old name wasn't put back" } },
    );
  };

  return (
    <>
      <PanelSection>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            rename();
          }}
        >
          <Field label="Name" error={trimmed === '' && draft !== null ? 'Give the tag a name.' : undefined}>
            {({ inputId, describedBy, isInvalid }) => <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={name} maxLength={60} onChange={(event) => setDraft(event.target.value)} />}
          </Field>
        </form>
        <p>
          {peopleText(row.peopleCount)} · tags sit on the person and follow them to every job.
          {row.isRetired && ' A retired tag stays on people but leaves the picker.'}
        </p>
      </PanelSection>
      <MergeSection kind="tag" from={{ id: row.id, name: row.name, version: row.version }} options={others.map((other) => ({ id: other.id, name: other.name }))} onMerged={onMerged} />
      <PanelSection>
        <PanelActions>
          {row.isRetired ? (
            <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setTagRetired(changes, row, false)}>
              Bring back
            </Button>
          ) : (
            <Button variant="ghost" size="sm" icon={Archive} onClick={() => setTagRetired(changes, row, true)}>
              Retire
            </Button>
          )}
          <Button variant="solid" size="sm" disabled={!isValid || !isChanged} onClick={rename}>
            Save name
          </Button>
        </PanelActions>
      </PanelSection>
    </>
  );
}

function PoolPanel({ row, others, changes, onMerged }: { readonly row: PoolRow; readonly others: readonly PoolRow[]; readonly changes: RowChanges<PoolRow>; readonly onMerged: (id: string) => void }): ReactNode {
  const canMerge = !row.isSystem && !row.isArchived;
  return (
    <>
      <PanelSection>
        <PanelFacts
          facts={[
            { label: 'People', value: formatNumber(row.memberCount) },
            { label: 'Owner', value: row.ownerName },
            { label: 'Contacted lately', value: row.freshnessPercent === null ? '—' : `${row.freshnessPercent}%` },
          ]}
        />
        <Link to="/pools/$poolId" params={{ poolId: row.id }}>
          Open the pool <ArrowUpRight size={14} aria-hidden />
        </Link>
        {row.isSystem && <p>The system’s own pool: other pools can be merged into it, but it can’t be merged away.</p>}
      </PanelSection>
      {canMerge && <MergeSection kind="pool" from={{ id: row.id, name: row.name, version: row.version }} options={others.map((other) => ({ id: other.id, name: other.name }))} onMerged={onMerged} />}
      {row.isArchived && (
        <PanelSection>
          <p>Archived: it is out of the pickers, and the people in it stay. Bring it back to use it again.</p>
          <PanelActions>
            <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setPoolArchived(changes, row, false)}>
              Bring back
            </Button>
          </PanelActions>
        </PanelSection>
      )}
    </>
  );
}

/**
 * "Merge into another …": the target, the server's preview in distinct people, and Merge — which can't be undone, so it
 * asks beside the button for the confirmation word. It shows at once and is put back, with the reason, if refused.
 */
function MergeSection({
  kind,
  from,
  options,
  onMerged,
}: {
  readonly kind: 'pool' | 'tag';
  readonly from: { readonly id: string; readonly name: string; readonly version: number };
  readonly options: readonly { readonly id: string; readonly name: string }[];
  readonly onMerged: (intoId: string) => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const [intoId, setIntoId] = useState<string | null>(null);
  const into = options.find((option) => option.id === intoId) ?? null;
  const preview = useMergePreview(kind, from.id, into?.id ?? null);
  const [isMerging, setIsMerging] = useState(false);

  const merge = async (counts: PoolMergePreview, target: { readonly id: string; readonly name: string }): Promise<void> => {
    setIsMerging(true);
    const before = queryClient.getQueryData<PoolsAndTagsView>(VIEW_KEY);
    if (before) queryClient.setQueryData(VIEW_KEY, merged(before, kind, from.id, target.id, counts));
    try {
      await (kind === 'pool' ? poolsApi.mergePool(from.id, target.id, from.version) : poolsApi.mergeTag(from.id, target.id, from.version));
      show({ tone: 'success', message: `“${from.name}” merged into “${target.name}”` });
      onMerged(target.id);
    } catch (cause) {
      // Put back just these two rows: anything else that changed meanwhile stays.
      const now = queryClient.getQueryData<PoolsAndTagsView>(VIEW_KEY);
      if (before && now) queryClient.setQueryData(VIEW_KEY, restored(now, before, [from.id, target.id]));
      show({ tone: 'danger', message: kind === 'pool' ? "The pools weren't merged" : "The tags weren't merged", detail: errorMessage(cause) });
    } finally {
      setIsMerging(false);
      await queryClient.invalidateQueries({ queryKey: POOLS_KEY });
    }
  };

  const noun = kind === 'pool' ? 'pool' : 'tag';
  return (
    <PanelSection title={`Merge into another ${noun}`}>
      {options.length === 0 ? (
        <p>There is no other {noun} in use to merge into.</p>
      ) : (
        <>
          <Field label="Merge into">
            {({ inputId, describedBy }) => (
              <Select id={inputId} describedBy={describedBy} label={`Merge ${from.name} into`} value={intoId} placeholder={`Choose a ${noun}…`} options={options.map((option) => ({ value: option.id, label: option.name }))} onChange={setIntoId} />
            )}
          </Field>
          {into && <MergeLine kind={kind} fromName={from.name} intoName={into.name} preview={preview.data} isLoading={preview.isFetching && !preview.data} error={preview.error ? errorMessage(preview.error) : null} />}
          <PanelActions>
            <span />
            <ConfirmTyped
              label="Merge"
              icon={GitMerge}
              variant="dangerSolid"
              disabled={!into || !preview.data || preview.isFetching}
              isPending={isMerging}
              title={into ? `Merge “${from.name}” into “${into.name}”?` : 'Merge'}
              body={into && preview.data ? <MergeLine kind={kind} fromName={from.name} intoName={into.name} preview={preview.data} isLoading={false} error={null} /> : null}
              word={MERGE_CONFIRM_WORD}
              confirmLabel="Merge"
              onConfirm={() => {
                if (into && preview.data) void merge(preview.data, into);
              }}
            />
          </PanelActions>
        </>
      )}
    </PanelSection>
  );
}

/** "4 people move to “Relocate to Gazipur” (31 → 35). …" — what the server counted, in distinct people. */
function MergeLine({ kind, fromName, intoName, preview, isLoading, error }: { readonly kind: 'pool' | 'tag'; readonly fromName: string; readonly intoName: string; readonly preview: PoolMergePreview | undefined; readonly isLoading: boolean; readonly error: string | null }): ReactNode {
  if (error !== null) return <p role="alert">{error}</p>;
  if (isLoading || !preview) return <p>Counting who would move…</p>;
  const already = preview.alreadyThere === 0 ? '' : ` ${peopleText(preview.alreadyThere)} already ${kind === 'pool' ? 'in it' : 'have it'}, so only their “${fromName}” ${kind === 'pool' ? 'place ends' : 'goes'}.`;
  const then = kind === 'pool' ? `Then “${fromName}” is archived. Nobody’s application changes.` : `Then “${fromName}” is deleted: it leaves every list and picker, and its history stays.`;
  return (
    <p>
      <strong>
        {peopleText(preview.moving)} {preview.moving === 1 ? 'moves' : 'move'} to “{intoName}” ({formatNumber(preview.intoBefore)} → {formatNumber(preview.intoAfter)}).
      </strong>
      {already} {then}
    </p>
  );
}

function setTagRetired(changes: RowChanges<TagAdminRow>, row: TagAdminRow, isRetired: boolean): void {
  const send = (retire: boolean) => async (version: number) => (await (retire ? poolsApi.retireTag(row.id, version) : poolsApi.restoreTag(row.id, version))).version;
  changes.changeWithUndo(
    { id: row.id, patch: { isRetired }, send: send(isRetired), failure: isRetired ? "The tag wasn't retired" : "The tag wasn't brought back" },
    { message: isRetired ? `“${row.name}” retired` : `“${row.name}” is back in use`, undo: { id: row.id, patch: { isRetired: !isRetired }, send: send(!isRetired), failure: "The change wasn't put back" } },
  );
}

function setPoolArchived(changes: RowChanges<PoolRow>, row: PoolRow, isArchived: boolean): void {
  const send = (archive: boolean) => async (version: number) => (await (archive ? poolsApi.archive(row.id, version) : poolsApi.restore(row.id, version))).version;
  changes.changeWithUndo(
    { id: row.id, patch: { isArchived }, send: send(isArchived), failure: isArchived ? "The pool wasn't archived" : "The pool wasn't brought back" },
    { message: isArchived ? `${row.name} archived` : `${row.name} is back`, undo: { id: row.id, patch: { isArchived: !isArchived }, send: send(!isArchived), failure: "The change wasn't put back" } },
  );
}

/** The view as it reads straight after a merge: the merged-away one gone (tag) or archived and empty (pool). */
function merged(view: PoolsAndTagsView, kind: 'pool' | 'tag', fromId: string, intoId: string, counts: PoolMergePreview): PoolsAndTagsView {
  if (kind === 'tag') return { ...view, tags: view.tags.filter((tag) => tag.id !== fromId).map((tag) => (tag.id === intoId ? { ...tag, peopleCount: counts.intoAfter } : tag)) };
  return {
    ...view,
    pools: view.pools.map((pool) => {
      if (pool.id === fromId) return { ...pool, isArchived: true, memberCount: 0 };
      return pool.id === intoId ? { ...pool, memberCount: counts.intoAfter } : pool;
    }),
  };
}

/** `now` with the rows of `ids` as they were in `before`, the merged-away tag put back where it was. */
function restored(now: PoolsAndTagsView, before: PoolsAndTagsView, ids: readonly string[]): PoolsAndTagsView {
  const keep = <TRow extends { readonly id: string }>(current: readonly TRow[], earlier: readonly TRow[]): readonly TRow[] =>
    earlier.flatMap((row) => {
      if (ids.includes(row.id)) return [row];
      const live = current.find((item) => item.id === row.id);
      return live ? [live] : [];
    }).concat(current.filter((row) => !earlier.some((item) => item.id === row.id)));
  return { pools: keep(now.pools, before.pools), tags: keep(now.tags, before.tags) };
}

function ordered<TRow>(rows: readonly TRow[], people: (row: TRow) => number, isFewestFirst: boolean): readonly TRow[] {
  return isFewestFirst ? [...rows].sort((a, b) => people(a) - people(b)) : rows;
}

function matchesOff(isOff: boolean, filter: Filter | null): boolean {
  if (filter === 'in_use') return !isOff;
  if (filter === 'off') return isOff;
  return true;
}

function matches(name: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || name.toLowerCase().includes(needle);
}

function peopleText(count: number): string {
  return `${formatNumber(count)} ${count === 1 ? 'person' : 'people'}`;
}
